// ──────────────────────────────────────────────────────────────────────
// Batch 5 R3 CT-013 — Brand Kit Tab (flagship)
// Mounted inside the client detail in MyClientsPage. Allows:
//   • Logo upload (drag-drop or click — PNG/JPG/SVG/WEBP, ≤10MB)
//   • Color swatches: add / edit (hex + label) / reorder / delete
//   • Fonts: pick a Google Font OR upload a custom .woff/.woff2/.ttf
//   • Brand guidelines PDF upload (single, ≤10MB)
//   • "Download as ZIP" — bundles logo + guidelines + fonts + swatches.json
//
// Edit rights: Owner / Admin / Account Manager (server enforces).
// ──────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Upload, Trash2, Plus, Loader2, Download, FileText, Image as ImageIcon, Type, Palette, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { brandKitAPI } from '../lib/api';
import { toast } from 'sonner';

// Common Google Fonts (curated subset — covers ~95% of typical brand needs).
const GOOGLE_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Source Sans 3',
  'Raleway', 'Nunito', 'Work Sans', 'Manrope', 'DM Sans', 'Plus Jakarta Sans',
  'Playfair Display', 'Merriweather', 'Lora', 'EB Garamond', 'Cormorant Garamond',
  'Oswald', 'Bebas Neue', 'Anton', 'Archivo', 'Karla', 'Mulish',
  'Space Grotesk', 'Outfit', 'Sora', 'Fira Sans', 'IBM Plex Sans', 'Geist',
];

export default function BrandKitTab({ clientId, workspaceId, canEdit }) {
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSwatches, setSavingSwatches] = useState(false);
  const [uploading, setUploading] = useState(null); // 'logo' | 'guidelines' | 'font' | null
  const [downloading, setDownloading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [customFontFamily, setCustomFontFamily] = useState('');
  const logoInputRef = useRef(null);
  const guidelinesInputRef = useRef(null);
  const fontInputRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!clientId || !workspaceId) return;
    setLoading(true);
    try {
      const res = await brandKitAPI.get(clientId, workspaceId);
      setKit(res.data);
      // Refresh logo blob URL
      if (res.data?.logo) {
        try {
          const url = await brandKitAPI.assetBlob(clientId, workspaceId, 'logo');
          setLogoUrl(url);
        } catch { setLogoUrl(''); }
      } else {
        setLogoUrl('');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load brand kit');
    } finally { setLoading(false); }
  }, [clientId, workspaceId]);

  useEffect(() => {
    refresh();
    return () => { if (logoUrl) URL.revokeObjectURL(logoUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // ---------- file uploads ----------
  const handleFileUpload = async (kind, file, family = '') => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setUploading(kind);
    try {
      const res = await brandKitAPI.uploadAsset(clientId, workspaceId, kind, file, family);
      setKit(res.data);
      if (kind === 'logo') {
        try {
          const url = await brandKitAPI.assetBlob(clientId, workspaceId, 'logo');
          if (logoUrl) URL.revokeObjectURL(logoUrl);
          setLogoUrl(url);
        } catch {}
      }
      toast.success(`${kind === 'logo' ? 'Logo' : kind === 'guidelines' ? 'Guidelines' : 'Font'} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(null); }
  };

  const handleDeleteAsset = async (kind, fontId = '') => {
    setUploading(kind);
    try {
      const res = await brandKitAPI.deleteAsset(clientId, workspaceId, kind, fontId);
      setKit(res.data);
      if (kind === 'logo' && logoUrl) { URL.revokeObjectURL(logoUrl); setLogoUrl(''); }
      toast.success('Removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally { setUploading(null); }
  };

  // ---------- drag-drop ----------
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDropLogo = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!canEdit) return;
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileUpload('logo', f);
  };

  // ---------- swatches ----------
  const updateSwatches = async (next) => {
    setSavingSwatches(true);
    try {
      const res = await brandKitAPI.patch(clientId, workspaceId, { swatches: next });
      setKit(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save swatches');
    } finally { setSavingSwatches(false); }
  };

  const addSwatch = () => {
    const cur = kit?.swatches || [];
    const next = [...cur, { hex: '#000000', label: `Color ${cur.length + 1}`, order: cur.length }];
    updateSwatches(next);
  };
  const editSwatch = (idx, patch) => {
    const cur = [...(kit?.swatches || [])];
    cur[idx] = { ...cur[idx], ...patch };
    setKit(prev => ({ ...prev, swatches: cur }));
  };
  const commitSwatch = (idx) => {
    const cur = kit?.swatches || [];
    updateSwatches(cur.map((s, i) => i === idx ? s : s));
  };
  const removeSwatch = (idx) => {
    const next = (kit?.swatches || []).filter((_, i) => i !== idx);
    updateSwatches(next);
  };

  // ---------- fonts ----------
  const pickGoogleFont = (family) => {
    if (!family) return;
    const exists = (kit?.fonts || []).some(f => f.family === family && f.source === 'google');
    if (exists) { toast.error(`${family} already added`); return; }
    const next = [...(kit?.fonts || []), { family, source: 'google' }];
    brandKitAPI.patch(clientId, workspaceId, { fonts: next })
      .then(res => { setKit(res.data); setShowFontPicker(false); toast.success(`${family} added`); })
      .catch(err => toast.error(err?.response?.data?.detail || 'Failed'));
  };
  const removeFont = (fontId) => handleDeleteAsset('font', fontId);

  // Inject <link> tags for Google Fonts so the family renders in the preview.
  useEffect(() => {
    const ids = new Set();
    (kit?.fonts || []).forEach(f => {
      if (f.source !== 'google') return;
      const id = `gfont-${f.family.replace(/\s+/g, '-')}`;
      ids.add(id);
      if (document.getElementById(id)) return;
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f.family)}:wght@400;600;700&display=swap`;
      document.head.appendChild(link);
    });
  }, [kit?.fonts]);

  // ---------- ZIP download ----------
  const handleDownloadZip = async () => {
    setDownloading(true);
    try {
      const blob = await brandKitAPI.downloadZip(clientId, workspaceId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brand-kit_${kit?.client_name || 'client'}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('ZIP download failed');
    } finally { setDownloading(false); }
  };

  const hasAnything = useMemo(() => {
    return !!(kit?.logo || kit?.guidelines || (kit?.swatches || []).length || (kit?.fonts || []).length);
  }, [kit]);

  if (loading) {
    return <div className="text-center py-12 text-[12px] text-muted-foreground/50" data-testid="brand-kit-loading"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading brand kit…</div>;
  }

  return (
    <div className="space-y-6" data-testid="brand-kit-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[16px] font-display font-semibold text-foreground">Brand Kit</h3>
          <p className="text-[11px] text-muted-foreground/50">Logo, color swatches, fonts and brand guidelines — all bundleable as a ZIP.</p>
        </div>
        <Button
          size="sm"
          disabled={downloading || !hasAnything}
          onClick={handleDownloadZip}
          className="bg-gold/15 hover:bg-gold/25 text-gold border-0"
          data-testid="brand-kit-download-zip"
        >
          {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
          Download as ZIP
        </Button>
      </div>

      {/* ── LOGO ── */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[12px] font-medium flex items-center gap-2"><ImageIcon className="h-3.5 w-3.5 text-gold/70" /> Logo</Label>
          {kit?.logo && canEdit && (
            <button onClick={() => handleDeleteAsset('logo')} className="text-[11px] text-red-400/70 hover:text-red-300 inline-flex items-center gap-1" data-testid="brand-kit-logo-remove">
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          )}
        </div>
        <div
          onDragOver={onDragOver}
          onDrop={onDropLogo}
          onClick={() => canEdit && logoInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-4 transition-colors ${canEdit ? 'cursor-pointer hover:border-gold/40 hover:bg-foreground/[0.02]' : ''} ${kit?.logo ? 'border-emerald-500/30 bg-emerald-500/[0.03]' : 'border-border/30'}`}
          data-testid="brand-kit-logo-dropzone"
        >
          {uploading === 'logo' ? (
            <div className="text-center py-8 text-[12px] text-muted-foreground/60"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Uploading…</div>
          ) : kit?.logo && logoUrl ? (
            <div className="flex items-center gap-4">
              <img src={logoUrl} alt="Brand logo" className="max-h-24 max-w-[200px] object-contain" data-testid="brand-kit-logo-preview" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-foreground truncate">{kit.logo.filename}</div>
                <div className="text-[10px] text-muted-foreground/40">{kit.logo.content_type} · uploaded {kit.logo.uploaded_at?.slice(0, 10)}</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <Upload className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
              <div className="text-[12px] text-muted-foreground/60">{canEdit ? 'Drop a logo here or click to upload' : 'No logo uploaded'}</div>
              <div className="text-[10px] text-muted-foreground/30 mt-1">PNG · JPG · SVG · WEBP · ≤10MB</div>
            </div>
          )}
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" hidden onChange={e => handleFileUpload('logo', e.target.files?.[0])} data-testid="brand-kit-logo-input" />
        </div>
      </section>

      {/* ── SWATCHES ── */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[12px] font-medium flex items-center gap-2"><Palette className="h-3.5 w-3.5 text-gold/70" /> Color swatches <span className="text-muted-foreground/40 font-normal">({(kit?.swatches || []).length})</span></Label>
          {canEdit && (
            <Button size="sm" variant="ghost" onClick={addSwatch} disabled={savingSwatches} className="h-7 text-[11px]" data-testid="brand-kit-swatch-add">
              <Plus className="h-3 w-3 mr-1" /> Add swatch
            </Button>
          )}
        </div>
        {(kit?.swatches || []).length === 0 ? (
          <div className="text-[12px] text-muted-foreground/40 italic py-3">No swatches yet{canEdit && ' — click "Add swatch"'}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="brand-kit-swatches">
            {(kit.swatches || []).map((s, i) => (
              <div key={s.id || i} className="border border-border/30 rounded-lg p-3 flex items-center gap-3 bg-card" data-testid={`brand-kit-swatch-${i}`}>
                <div className="h-12 w-12 rounded-md border border-border/40 shrink-0" style={{ backgroundColor: s.hex }} />
                <div className="flex-1 min-w-0 space-y-1">
                  <Input
                    value={s.label}
                    onChange={e => editSwatch(i, { label: e.target.value })}
                    onBlur={() => commitSwatch(i)}
                    placeholder="Label"
                    disabled={!canEdit}
                    className="h-7 text-[12px] bg-foreground/[0.02] border-border/30"
                    data-testid={`brand-kit-swatch-label-${i}`}
                  />
                  <Input
                    value={s.hex}
                    onChange={e => editSwatch(i, { hex: e.target.value })}
                    onBlur={() => commitSwatch(i)}
                    placeholder="#RRGGBB"
                    disabled={!canEdit}
                    className="h-7 text-[11px] font-mono bg-foreground/[0.02] border-border/30 uppercase"
                    data-testid={`brand-kit-swatch-hex-${i}`}
                  />
                </div>
                {canEdit && (
                  <button onClick={() => removeSwatch(i)} className="p-1 rounded hover:bg-red-500/10 text-red-400/60" data-testid={`brand-kit-swatch-remove-${i}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── FONTS ── */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[12px] font-medium flex items-center gap-2"><Type className="h-3.5 w-3.5 text-gold/70" /> Fonts <span className="text-muted-foreground/40 font-normal">({(kit?.fonts || []).length})</span></Label>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowFontPicker(s => !s)} className="h-7 text-[11px]" data-testid="brand-kit-font-add-google">
                <Plus className="h-3 w-3 mr-1" /> Google Font
              </Button>
              <Button size="sm" variant="ghost" onClick={() => fontInputRef.current?.click()} className="h-7 text-[11px]" data-testid="brand-kit-font-add-custom">
                <Upload className="h-3 w-3 mr-1" /> Upload font
              </Button>
              <input
                ref={fontInputRef}
                type="file"
                accept=".woff,.woff2,.ttf,.otf,font/woff,font/woff2"
                hidden
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const family = customFontFamily.trim() || (f.name || 'Custom').replace(/\.[^.]+$/, '');
                  handleFileUpload('font', f, family);
                  setCustomFontFamily('');
                  e.target.value = '';
                }}
                data-testid="brand-kit-font-input"
              />
            </div>
          )}
        </div>
        {showFontPicker && canEdit && (
          <div className="border border-border/30 rounded-lg p-3 bg-foreground/[0.02] max-h-56 overflow-auto" data-testid="brand-kit-font-picker">
            <p className="text-[10px] text-muted-foreground/50 mb-2">Click a Google Font to add it:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {GOOGLE_FONTS.map(f => (
                <button
                  key={f}
                  onClick={() => pickGoogleFont(f)}
                  className="text-left px-2 py-1.5 rounded-md text-[12px] hover:bg-foreground/[0.05] text-foreground/80"
                  data-testid={`brand-kit-font-option-${f.replace(/\s+/g, '-')}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        )}
        {canEdit && (
          <div className="text-[10px] text-muted-foreground/40 flex items-center gap-2">
            <span>Custom font family name:</span>
            <Input
              value={customFontFamily}
              onChange={e => setCustomFontFamily(e.target.value)}
              placeholder="e.g. Stellar Display"
              className="h-6 w-48 text-[11px] bg-foreground/[0.02] border-border/30"
              data-testid="brand-kit-custom-font-family"
            />
            <span className="text-muted-foreground/30">(then click "Upload font")</span>
          </div>
        )}
        {(kit?.fonts || []).length === 0 ? (
          <div className="text-[12px] text-muted-foreground/40 italic py-3">No fonts yet{canEdit && ' — pick a Google Font or upload a custom .woff/.woff2'}</div>
        ) : (
          <div className="space-y-2" data-testid="brand-kit-fonts">
            {(kit.fonts || []).map((f) => (
              <div key={f.id || f.family} className="border border-border/30 rounded-lg p-3 flex items-center gap-3 bg-card" data-testid={`brand-kit-font-${f.id || f.family}`}>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[18px] font-semibold text-foreground truncate"
                    style={{ fontFamily: f.source === 'google' ? `"${f.family}", sans-serif` : 'inherit' }}
                  >
                    {f.family}
                  </div>
                  <div className="text-[10px] text-muted-foreground/40">
                    {f.source === 'google' ? 'Google Fonts' : `Custom · ${f.filename || ''}`}
                  </div>
                </div>
                {canEdit && (
                  <button onClick={() => removeFont(f.id)} className="p-1 rounded hover:bg-red-500/10 text-red-400/60" data-testid={`brand-kit-font-remove-${f.id || f.family}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── BRAND GUIDELINES PDF ── */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[12px] font-medium flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-gold/70" /> Brand guidelines (PDF)</Label>
          {kit?.guidelines && canEdit && (
            <button onClick={() => handleDeleteAsset('guidelines')} className="text-[11px] text-red-400/70 hover:text-red-300 inline-flex items-center gap-1" data-testid="brand-kit-guidelines-remove">
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          )}
        </div>
        <div
          onClick={() => canEdit && guidelinesInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-4 transition-colors ${canEdit ? 'cursor-pointer hover:border-gold/40 hover:bg-foreground/[0.02]' : ''} ${kit?.guidelines ? 'border-emerald-500/30 bg-emerald-500/[0.03]' : 'border-border/30'}`}
          data-testid="brand-kit-guidelines-dropzone"
        >
          {uploading === 'guidelines' ? (
            <div className="text-center py-6 text-[12px] text-muted-foreground/60"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Uploading…</div>
          ) : kit?.guidelines ? (
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-emerald-400/70 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-foreground truncate">{kit.guidelines.filename}</div>
                <div className="text-[10px] text-muted-foreground/40">PDF · uploaded {kit.guidelines.uploaded_at?.slice(0, 10)}</div>
              </div>
              <a
                href={brandKitAPI.assetUrl(clientId, workspaceId, 'guidelines')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="text-[11px] text-gold hover:underline"
                data-testid="brand-kit-guidelines-open"
              >
                Open →
              </a>
            </div>
          ) : (
            <div className="text-center py-6">
              <FileText className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
              <div className="text-[12px] text-muted-foreground/60">{canEdit ? 'Click to upload guidelines PDF' : 'No guidelines uploaded'}</div>
              <div className="text-[10px] text-muted-foreground/30 mt-1">PDF · ≤10MB</div>
            </div>
          )}
          <input ref={guidelinesInputRef} type="file" accept="application/pdf" hidden onChange={e => handleFileUpload('guidelines', e.target.files?.[0])} data-testid="brand-kit-guidelines-input" />
        </div>
      </section>
    </div>
  );
}
